/**
 * TanStack queryOptions factories — Phase 4-2 / T9.
 *
 * One factory per backend resource. Each returns a `queryOptions` object that
 * Phase 4-3 components will pass to `createQuery(...)`.
 *
 * Response shapes are best-effort guesses based on the v2 backend SSO bootstrap
 * sequence in the plan; Phase 4-3 will tighten them against the real
 * widget-api / host adapter responses.
 *
 * Importers: src/lib/embed/bootstrap.ts (calls fetchQuery(ssoExchange(...))).
 * Phase 4-3 list/thread/host-summary components.
 */

import { queryOptions } from '@tanstack/svelte-query';
import { http } from './http.js';

// ---------------------------------------------------------------------------
// Response types — provisional, sharpened in Phase 4-3.
// ---------------------------------------------------------------------------

export interface SsoTenant {
  id: string;
  code: string;
  name?: string;
  /**
   * Admin-saved tenant theme tokens (Phase 6-1). Applied at bootstrap before
   * host-pushed `init.theme` so host overrides take precedence.
   */
  themeTokens?: Record<string, string> | null;
}

export interface SsoUser {
  id: string;
  email: string;
  displayName?: string;
  role?: string;
}

export interface SsoTheme {
  colorBg?: string;
  colorFg?: string;
  colorAccent?: string;
  colorBorder?: string;
  fontFamily?: string;
  radius?: string;
  [key: string]: string | undefined;
}

/**
 * Per-entity-type capability descriptor surfaced by the host adapter.
 * `formSchema` is a JSON-serializable schema consumed by CreateEntityModal
 * (Phase 4-7); shape is intentionally loose at this layer.
 */
export interface SsoEntityTypeCapability {
  entityType: string;
  displayName: string;
  formSchema?: unknown;
}

export interface SsoCapabilities {
  entityTypes?: SsoEntityTypeCapability[];
}

export interface SsoResponse {
  /** Widget session token to send back as X-Widget-Auth. */
  sessionToken: string;
  /** Origins allowed to embed this tenant's widget. */
  embedOrigins: string[];
  tenant: SsoTenant;
  user: SsoUser;
  theme?: SsoTheme;
  /** Capabilities surfaced by the host adapter (e.g. ['createCustomer']). */
  hostCapabilities?: string[];
  /** Structured per-entity-type capabilities (Phase 4-6 / T32). */
  sso?: {
    capabilities?: SsoCapabilities;
  };
}

/**
 * G1.6 — dense tag ref embedded on each conversation row for chip
 * rendering in the list. Matches the server response shape (id/name/color
 * only — tenantId + timestamps live on the full Tag catalog record).
 */
export interface TagRef {
  id: string;
  name: string;
  color: string;
}

/**
 * G1.6 / G1.7 — full catalog record returned by GET /api/tags. The
 * picker iterates this list, checking off whichever ids are already in
 * the conversation's appliedTags. G1.6 uses it for filter chip lookups;
 * G1.7 picker/manage modal mutate it.
 */
export interface Tag {
  id: string;
  tenantId: string;
  name: string;
  slug: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface TagsListResponse {
  items: Tag[];
}

/**
 * G4.1 — dense assignee ref embedded on each conversation row. Null when
 * the conversation is unassigned. displayName may be null when the
 * server's identity lookup failed (rare); UI falls back to a generic
 * "Operator" label, matching message-bubble convention.
 *
 * There is no expiry field. Assignments used to carry one and a sweeper
 * released them; both were removed on 2026-09-07, and a client type that
 * still admitted `claimExpiresAt` would keep a countdown alive in the UI
 * long after the server stopped sending anything to count.
 */
export interface AssigneeRef {
  userId: string;
  displayName: string | null;
  assignedAt: string;
  assignedByUserId: string;
}

/**
 * G4.2a — minimal candidate shape for the assignee picker. Returned by
 * GET /api/admin/tenants/:tenantId/assignable-operators. Intentionally
 * narrower than IdentityUser — operators can't enumerate sensitive
 * metadata (email, status, role) via the picker.
 */
export interface AssignableOperator {
  userId: string;
  displayName: string;
}

export interface Conversation {
  id: string;
  pageId: string;
  customerId: string | null;
  customerName?: string | null;
  customerProfilePicUrl?: string | null;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  unreadCount?: number;
  /**
   * G1.6 — applied tag refs for chip rendering. May be undefined on
   * older payloads or absent from raw shapes that bypass the standard
   * list endpoint; treat as empty when missing. Server batch-fetches per
   * page so this doesn't N+1.
   */
  appliedTags?: TagRef[];
  /**
   * G3.1 — first-response SLA. awaitingResponseSince is the moment an
   * inbound customer message arrived without an outbound reply since
   * (null = no clock running, operator has already replied). slaBreached
   * is server-computed against the tenant-wide threshold so all clients
   * agree on the cutoff without coupling to env config. Both optional
   * for backward compat with payloads pre-G3.1.
   */
  awaitingResponseSince?: string | null;
  slaBreached?: boolean;
  /**
   * "Can I act on this at all?" — a different question from the SLA clock, and
   * often true at the same time (a dead send on a 42-day wait). Both are
   * server-computed so every client agrees, and so the operator never has to
   * open a thread to discover it is unanswerable.
   *
   * sendFailed: the latest OUTBOUND message exhausted its outbox retries.
   * windowClosed: Meta's 24-hour reply window has passed. META channel only —
   * the widget and internal threads have no such limit.
   *
   * Both optional for backward compat with payloads that predate them.
   */
  sendFailed?: boolean;
  windowClosed?: boolean;
  /**
   * G4.1 — durable operator assignment. Null = unassigned. Optional for
   * backward compat with payloads pre-G4.1. Server batch-resolves
   * displayName per page so this doesn't N+1.
   */
  assignee?: AssigneeRef | null;
  /** META, WIDGET, INTERNAL. Drives the per-row channel chip. */
  /**
   * OPEN or CLOSED. The server has always sent it; nothing here read it,
   * which is why a finished conversation still offered a reply box.
   * Optional because raw shapes that bypass the list endpoint omit it —
   * treat a missing value as OPEN, the safer of the two: showing a composer
   * on an open thread is right, hiding one on a closed thread is right, and
   * guessing CLOSED would silently take the reply box away.
   */
  status?: string;
  channelType?: string;
  externalPageId?: string | null;
  /**
   * Human-readable Page name from identity-service, surfaced on the row
   * so multi-page operators can tell rows apart at a glance.
   */
  pageName?: string | null;
  externalThreadRef?: string | null;
  tenantId?: string;
}

export interface MessageReactionSlot {
  emoji: string;
  /** ISO 8601 UTC. */
  at: string;
}

export interface MessageReactions {
  customer?: MessageReactionSlot;
  operator?: MessageReactionSlot;
}

export interface Message {
  id: string;
  conversationId: string;
  direction: 'inbound' | 'outbound';
  type: 'text' | 'image' | 'video' | 'system';
  text?: string;
  attachmentUrl?: string;
  createdAt: string;
  operatorId?: string;
  /**
   * Phase 3: customer reactions (populated by messaging_reactions webhook).
   * Phase 4 will add operator reactions. Absent when no reactions on the
   * message.
   */
  reactions?: MessageReactions;
  /**
   * Sender attribution on outbound bubbles. Populated server-side from the
   * MessageCoreParticipant author row, seeded at first send from identity-
   * service. Null when the identity lookup hadn't succeeded yet — widget
   * falls back to a generic "Operator" label.
   */
  authorDisplayName?: string | null;
  authorProfilePicUrl?: string | null;
}

/**
 * Customer Context Panel response — mirrors @platform/contracts
 * `HostSummaryResponse` (libs/contracts/src/messages/host-summary.ts).
 * Phase 4-5 ships the backend; Phase 4-6 renders this in the widget.
 */
export type HostMatchStatus = 'AUTO' | 'MANUAL' | 'UNMATCHED' | 'DECLINED';

export interface HostSummaryField {
  label: string;
  value: string;
}

export interface HostSummaryActivity {
  at: string;
  text: string;
}

export interface HostCustomerSummary {
  displayName: string;
  badges?: string[];
  fields: HostSummaryField[];
  recentActivity?: HostSummaryActivity[];
}

export interface HostLinkedEntity {
  entityType: string;
  entityId: string;
  deepLink: string | null;
  createdAt: string;
}

export interface HostSummary {
  matchStatus: HostMatchStatus;
  summary: HostCustomerSummary | null;
  linkedEntities: HostLinkedEntity[];
}

export interface RealtimeTicket {
  token: string;
  wsUrl: string;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Query factories
// ---------------------------------------------------------------------------

export interface SsoExchangeInput {
  hostProductCode: string;
  hostUserJwt: string;
}

/**
 * SSO exchange — POSTed once at bootstrap to swap host JWT for a widget
 * session. `staleTime: Infinity` because we only ever fetch once per session.
 */
export const ssoExchange = (input: SsoExchangeInput) =>
  queryOptions<SsoResponse>({
    queryKey: ['embed', 'sso', input.hostProductCode],
    queryFn: () => http.post<SsoResponse>('/api/embed/sso', input),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 0,
  });

/**
 * Standalone CRM login — used by the widget when rendered as a top-level
 * page (widget.messengerapp.ge) instead of an embedded iframe. Mirrors the
 * embed-sso response shape so the existing session store can consume it.
 */
export interface CrmLoginRequest {
  email: string;
  password: string;
}
export interface CrmLoginMembership {
  tenantId: string;
  tenantCode: string;
  tenantDisplayName: string;
  role: string;
}
export interface CrmLoginResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  memberships: CrmLoginMembership[];
  controlPlaneRoles: string[];
  sessionToken: string;
}
export const crmLogin = (body: CrmLoginRequest) =>
  http.post<CrmLoginResponse>('/api/crm/session/login', body);

/**
 * Conversations list scoped to the operator's accessible pages. G1.7
 * accepts an optional tagIds filter; when present, queryKey includes
 * the filter so each distinct selection caches separately. Sorted
 * encoding keeps the key stable regardless of input ordering.
 */
export interface ConversationsListFilters {
  tagIds?: string[];
  /**
   * G4.1 — assignee filter. Accepts the server `?assignedTo=` values:
   * 'me' (caller's conversations), 'unassigned', or a specific operator's
   * UUID. Omit for no filter.
   */
  assignedTo?: 'me' | 'unassigned' | string;
  /**
   * Which queue. The server defaults to `open` when this is absent, which is
   * the right default for a work queue — but it also means a conversation
   * somebody finished disappears with no way to reach it. That is what this
   * is for.
   */
  status?: 'open' | 'closed';
}

export const conversationsList = (filters?: ConversationsListFilters | string[]) => {
  // Back-compat: existing call sites pass a bare `tagIds: string[]`. Detect
  // and coerce to the new ConversationsListFilters shape. New call sites
  // pass `{ tagIds, assignedTo }` directly.
  const f: ConversationsListFilters | undefined = Array.isArray(filters)
    ? { tagIds: filters }
    : filters;
  const sortedTagIds = f?.tagIds && f.tagIds.length > 0 ? [...f.tagIds].sort() : null;
  const assignedTo = f?.assignedTo ?? null;
  // Only 'closed' travels: 'open' is the server's default and adding it to
  // the key would split the cache between two identical result sets.
  const status = f?.status === 'closed' ? 'closed' : null;
  // queryKey must capture every filter that changes the result set so each
  // distinct combination caches independently. Only include the slots that
  // are set so unfiltered queries keep their stable, simple key.
  const keySlots: Record<string, unknown> = {};
  if (sortedTagIds) keySlots.tagIds = sortedTagIds;
  if (assignedTo) keySlots.assignedTo = assignedTo;
  if (status) keySlots.status = status;
  const queryKey: readonly unknown[] =
    Object.keys(keySlots).length > 0
      ? (['conversations', keySlots] as const)
      : (['conversations'] as const);
  return queryOptions<Conversation[]>({
    queryKey,
    queryFn: async () => {
      const params: string[] = [];
      if (sortedTagIds) {
        params.push(`tagIds=${sortedTagIds.map((id) => encodeURIComponent(id)).join(',')}`);
      }
      if (assignedTo) {
        params.push(`assignedTo=${encodeURIComponent(assignedTo)}`);
      }
      if (status) {
        params.push(`status=${status}`);
      }
      const qs = params.length > 0 ? `?${params.join('&')}` : '';
      const res = await http.get<{ items: Conversation[] } | Conversation[]>(
        `/api/conversations${qs}`,
      );
      return Array.isArray(res) ? res : (res?.items ?? []);
    },
  });
};

/**
 * One conversation, by id.
 *
 * The thread pane used to read its row out of the LIST cache, which quietly
 * made the thread depend on the inbox's current filter. Close a conversation
 * and it leaves the open queue — and with it the pane's only source of
 * `status`, so the closed state could never render and the composer came
 * back. Same for a thread opened from a push notification while a tag filter
 * is on.
 *
 * The list row stays as the instant first paint; this replaces it as soon as
 * it arrives.
 */
export const conversationDetail = (conversationId: string | null) =>
  queryOptions<Conversation | null>({
    queryKey: ['conversation', conversationId ?? ''],
    enabled: Boolean(conversationId),
    queryFn: async () => {
      if (!conversationId) return null;
      return http.get<Conversation>(`/api/conversations/${conversationId}`);
    },
  });

/**
 * G4.2a — operator candidates for the assignee picker. Filtered
 * server-side to ACTIVE users with an ACTIVE membership in the
 * tenant. `enabled` gated on tenantId so the picker can render
 * conditionally without firing an empty-path request.
 */
export const assignableOperators = (tenantId: string | null | undefined) =>
  queryOptions<AssignableOperator[]>({
    queryKey: ['assignable-operators', tenantId ?? ''],
    queryFn: () =>
      http.get<AssignableOperator[]>(
        `/api/admin/tenants/${encodeURIComponent(tenantId!)}/assignable-operators`,
      ),
    enabled: Boolean(tenantId),
    // Operator roster changes rarely during a session; 5-min stale window
    // amortises picker opens across the conversation switch flow.
    staleTime: 5 * 60_000,
  });

/**
 * G1.6 — per-tenant tag catalog. Used by G1.7's picker and manage modal
 * (catalog list of all tags the operator can apply). Landing the
 * query factory here in G1.6 keeps the surface area together; the
 * filter bar in G1.7 will read the same cache.
 */
export const tagsList = () =>
  queryOptions<Tag[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const res = await http.get<TagsListResponse>('/api/tags');
      return res.items;
    },
  });

/**
 * Tenant-shared quick reply templates. Operators trigger these from the
 * composer's `/slug` picker. The server scopes the list to the queried
 * tenant after verifying the operator's membership; `enabled` gates on
 * tenantId so callers can hand in `session.state.sso?.tenant.id`
 * without an `as string` cast.
 *
 * Templates change rarely — a 5-minute staleTime is fine and avoids
 * refetching every time the composer opens. The admin-console manage
 * page invalidates via window-focus refetch when the operator returns.
 */
export interface QuickReply {
  id: string;
  tenantId: string;
  slug: string;
  name: string;
  body: string;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
}

interface QuickRepliesListResponse {
  items: QuickReply[];
}

export const quickReplies = (tenantId: string | null | undefined) =>
  queryOptions<QuickReply[]>({
    queryKey: ['quick-replies', tenantId ?? ''],
    queryFn: async () => {
      const res = await http.get<QuickRepliesListResponse>(
        `/api/quick-replies?tenantId=${encodeURIComponent(tenantId!)}`,
      );
      return res.items;
    },
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
  });

/**
 * G2.3 — operator-only internal notes for a conversation. authorDisplayName
 * is pre-resolved server-side; clients render without an identity lookup.
 * Sorted newest-first by the server.
 */
export interface Note {
  id: string;
  conversationId: string;
  tenantId: string;
  authorUserId: string;
  authorDisplayName: string | null;
  body: string;
  createdAt: string;
}

export interface NotesListResponse {
  items: Note[];
}

/** Notes for a single conversation. Disabled until the parent passes an id. */
export const conversationNotes = (conversationId: string) =>
  queryOptions<Note[]>({
    queryKey: ['conversations', conversationId, 'notes'],
    queryFn: async () => {
      const res = await http.get<NotesListResponse>(
        `/api/conversations/${conversationId}/notes`,
      );
      return res.items;
    },
    enabled: Boolean(conversationId),
  });

/** Messages for a single conversation. */
interface RawMessage {
  id: string;
  conversationId: string;
  direction?: string;
  messageType?: string;
  type?: string;
  body?: string;
  text?: string;
  attachmentUrl?: string;
  createdAt: string;
  authorParticipantId?: string;
  operatorId?: string;
  reactions?: MessageReactions | null;
  authorDisplayName?: string | null;
  authorProfilePicUrl?: string | null;
}

function normalizeMessage(raw: RawMessage): Message {
  const direction = (raw.direction ?? '').toLowerCase();
  const type = (raw.type ?? raw.messageType ?? 'TEXT').toLowerCase();
  return {
    id: raw.id,
    conversationId: raw.conversationId,
    direction: direction === 'outbound' ? 'outbound' : 'inbound',
    type: (['text', 'image', 'video', 'system'] as const).includes(
      type as 'text' | 'image' | 'video' | 'system',
    )
      ? (type as Message['type'])
      : 'text',
    text: raw.text ?? raw.body,
    attachmentUrl: raw.attachmentUrl,
    createdAt: raw.createdAt,
    operatorId: raw.operatorId,
    reactions: raw.reactions ?? undefined,
    authorDisplayName: raw.authorDisplayName ?? null,
    authorProfilePicUrl: raw.authorProfilePicUrl ?? null,
  };
}

export const conversationMessages = (conversationId: string) =>
  queryOptions<Message[]>({
    queryKey: ['conversations', conversationId, 'messages'],
    queryFn: async () => {
      const res = await http.get<{ items: RawMessage[] } | RawMessage[]>(
        `/api/conversations/${conversationId}/messages`,
      );
      const items = Array.isArray(res) ? res : (res?.items ?? []);
      return items.map(normalizeMessage);
    },
    enabled: Boolean(conversationId),
  });

/** Host-side summary for a conversation (customer card, related entities). */
export const hostSummary = (conversationId: string) =>
  queryOptions<HostSummary>({
    queryKey: ['conversations', conversationId, 'host-summary'],
    queryFn: () => http.get<HostSummary>(`/api/conversations/${conversationId}/host-summary`),
    staleTime: 60_000,
    enabled: Boolean(conversationId),
  });

/**
 * Meta-side customer profile (always-on, independent of host-summary). Sourced
 * from the MessageCoreParticipant CUSTOMER row, populated by channels-meta's
 * Meta user-profile lookup at first sight of the conversation.
 */
export interface CustomerProfile {
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  profilePicUrl: string | null;
  externalSenderRef: string | null;
  /**
   * Phase 2b — populated when the customer taps a user_phone_number Quick
   * Reply chip in Messenger. Null until they share. Format is whatever Meta
   * delivered (typically E.164 with the leading `+`).
   */
  phoneNumber: string | null;
  /**
   * Channel source for this conversation. Lets the panel show a "Channel"
   * row like "Facebook · <pageId>". `channelType` mirrors the Conversation
   * field; `externalPageId` is the Page ID for META channels, null otherwise.
   */
  channelType: string;
  externalPageId: string | null;
  /**
   * Human-readable page name from identity-service. Null when the lookup
   * failed or the channel has no page concept. Panel renders this as the
   * headline; externalPageId becomes a small caption.
   */
  pageName: string | null;
}

export const customerProfile = (conversationId: string) =>
  queryOptions<CustomerProfile>({
    queryKey: ['conversations', conversationId, 'customer-profile'],
    queryFn: () =>
      http.get<CustomerProfile>(`/api/conversations/${conversationId}/customer-profile`),
    staleTime: 60_000,
    enabled: Boolean(conversationId),
  });

/**
 * Phase 8.1 / M8.1.6 — AI conversation summary.
 *
 * The endpoint is POST because it can have a side effect (persist a
 * freshly generated summary), but most calls return server-side cache
 * hits with `fromCache: true` and no LLM round-trip — so wrapping it as
 * a TanStack query is appropriate: identical request → identical result
 * across mounts until something invalidates it.
 *
 * Invalidation is driven from `App.svelte` (M8.1.8) on:
 *   - `conversation.assignee.updated` where the new assignee is the
 *     current operator (the "I just claimed" trigger — query becomes
 *     enabled and fires for the first time, OR re-fires after a stale
 *     marker landed during the previous claim),
 *   - `message.created` of direction=INBOUND for the active thread
 *     (the cached summary is now stale; refire to grab the regenerated
 *     copy on next read).
 *
 * Caller MUST pass `enabled: currentUserId === assignee.userId` so the
 * query doesn't fire for operators just browsing someone else's thread.
 * If we let it fire for non-assignees, we'd burn LLM tokens (and rate
 * limit) on every conversation an admin clicks.
 */
export interface AiSummary {
  summary: string;
  /** ISO timestamp of when the cached row was written. */
  generatedAt: string;
  fromCache: boolean;
  provider: 'anthropic' | 'openai';
  /** Null on cache hits — model that wrote the cached text isn't persisted. */
  model: string | null;
}

export const aiSummary = (conversationId: string, opts: { enabled: boolean }) =>
  queryOptions<AiSummary>({
    queryKey: ['conversations', conversationId, 'ai-summary'],
    queryFn: () =>
      http.post<AiSummary>(`/api/conversations/${conversationId}/ai-summary`, {}),
    enabled: opts.enabled && Boolean(conversationId),
    // Server returns cached hits cheaply, but the query still triggers
    // a network round-trip + DB read per mount without this. The
    // banner is a sticky thread-level surface; 5 min keeps it pinned
    // for the typical claim session.
    staleTime: 5 * 60_000,
    // 403 / 409 are terminal states (no scope, flag off, no provider,
    // no messages, LLM upstream failed). Retrying would just pile up
    // the same failure — show the error once and stop.
    retry: false,
  });

/** Short-lived ticket for the realtime (SSE) connection. */
export const realtimeTicket = () =>
  queryOptions<RealtimeTicket>({
    queryKey: ['realtime', 'ticket'],
    queryFn: () => http.get<RealtimeTicket>('/api/realtime/ticket'),
    staleTime: 30_000,
  });

/**
 * Signed URL for a message attachment.
 *
 * The attachment bucket is private, so `message.attachmentUrl` is a stable
 * identifier rather than something a browser can load. This fetches a
 * short-lived presigned GET for it.
 *
 * It has to be a separate request rather than a URL on the message payload:
 * an `<img>` tag can't carry the `X-Widget-Auth` header the widget
 * authenticates with (that header exists because Safari and Firefox drop
 * cookies in a cross-site iframe), so the URL must be fetched through the
 * normal HTTP client and then assigned to the element.
 *
 * `staleTime` sits well inside the server's TTL so a cached entry is always
 * refreshed before the URL it holds can expire.
 */
export interface AttachmentUrlResponse {
  url: string;
  expiresInSeconds: number;
}

export const attachmentUrl = (
  conversationId: string | null | undefined,
  messageId: string | null | undefined,
) =>
  queryOptions<AttachmentUrlResponse>({
    queryKey: ['attachment-url', conversationId ?? '', messageId ?? ''],
    queryFn: () =>
      http.get<AttachmentUrlResponse>(
        `/api/conversations/${encodeURIComponent(conversationId!)}/messages/${encodeURIComponent(messageId!)}/attachment`,
      ),
    enabled: Boolean(conversationId) && Boolean(messageId),
    // Server mints 15-min URLs; refetch at 10 so an on-screen image never
    // holds a dead link. gcTime matches so off-screen entries don't linger
    // past their usefulness.
    staleTime: 10 * 60_000,
    gcTime: 10 * 60_000,
    // A signed URL that 403s is not worth hammering — one retry covers a
    // transient blip, beyond that the alt text carries the failure.
    retry: 1,
  });
